import './coapplicantDifferentResidenceTypePage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { ResidenceTypeEnum } from '../../../store/applicantStore/enums/ResidenceTypeEnum';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentResidenceType,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceType,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      residenceTypeValue: coapplicantCurrentResidenceType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<ResidenceTypeEnum>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const residenceTypeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantCurrentResidenceType) && isValidClickEvent(event),
    );

    setCoapplicantCurrentResidenceType(residenceTypeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { residenceTypeValue } = values;

    if (!residenceTypeValue) {
      return;
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-different-residence-type-page"
        sx={theme.pageLayout}
      >
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={
              locator.pages.coapplicantDifferentResidenceType.form.root
            }
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantDifferentResidenceType.form.h1
                }
              >
                {t('CoapplicantDifferentResidenceTypePage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantDifferentResidenceType.form.h2
                }
              >
                {t('CoapplicantDifferentResidenceTypePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantDifferentResidenceType.alert
                        .error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  name="residenceTypeValue"
                  options={[
                    {
                      label: t('CoapplicantDifferentResidenceTypePage.own'),
                      value: ResidenceTypeEnum.OWN,
                      testId:
                        locator.pages.coapplicantDifferentResidenceType.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantDifferentResidenceTypePage.rent'),
                      value: ResidenceTypeEnum.RENT,
                      testId:
                        locator.pages.coapplicantDifferentResidenceType.form
                          .radioOption2,
                    },
                    {
                      label: t('CoapplicantDifferentResidenceTypePage.other'),
                      value: ResidenceTypeEnum.OTHER,
                      testId:
                        locator.pages.coapplicantDifferentResidenceType.form
                          .radioOption3,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantDifferentResidenceType.form
                      .radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantDifferentResidenceType.form
                      .submitButton
                  }
                  disabled={isEmpty(coapplicantCurrentResidenceType)}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantDifferentResidenceTypePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantDifferentResidenceType.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantDifferentResidenceTypePage';
